// client/src/features/vendorInvoices/paymentOnReceiptForm.ts
//
// The form side of "was this invoice already paid when it arrived?" (D49).
// The RULES are utils/paymentOnReceipt.ts, a twin of the server's; this file only
// turns what a person typed into the shape the rules read, and turns a stored
// answer back into words. Nothing here decides what is valid.

import { formatDate, storedDay } from '../../utils/dateFormat';
import type { DialogContent } from '../../utils/errorPresentation';
import { num } from '../../utils/invoiceLineMath';
import {
  PAYMENT_METHODS,
  latestPaymentDay,
  parsePaymentOnReceipt,
  type PaymentOnReceipt,
  type PaymentOnReceiptInput,
  type PaymentOnReceiptStatus,
} from '../../utils/paymentOnReceipt';

/** What the fields hold while someone is typing. `status: ''` is "not answered". */
export interface PaymentDraft {
  status: '' | PaymentOnReceiptStatus;
  paidDate: string;
  method: string;
  reference: string;
  amount: string;
}

/** No default answer, on purpose: the upload refuses a file until someone picks one. */
export const EMPTY_PAYMENT_DRAFT: PaymentDraft = Object.freeze({
  status: '',
  paidDate: '',
  method: '',
  reference: '',
  amount: '',
}) as PaymentDraft;

export { PAYMENT_METHODS };

/**
 * The browser's own calendar day, `yyyy-mm-dd`, for the latest-payment-day check. The server re-checks against the Trinidad business
 * day, so a browser in another time zone can be off by a day here and never wrong
 * there.
 */
export function browserToday(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** A payment date input's `max`: the latest day a payment may be dated (D67), from the browser's today. */
export function browserLatestPaymentDay(now: Date = new Date()): string {
  return latestPaymentDay(browserToday(now)) as string;
}

/**
 * The draft as the rules read it. `includeAmount` is false at upload: the invoice
 * has not been read yet, so a part-paid amount is typed on the review screen.
 */
export function draftToInput(draft: PaymentDraft, { includeAmount }: { includeAmount: boolean }): PaymentOnReceiptInput {
  return {
    status: draft.status,
    paidDate: draft.paidDate,
    method: draft.method,
    reference: draft.reference,
    amount: includeAmount ? num(draft.amount) : null,
  };
}

export type DraftCheck =
  | { ok: true; input: PaymentOnReceiptInput; value: PaymentOnReceipt }
  | { ok: false; code: string; message: string };

/** Is this draft an answer the server will accept? Same rule, asked here first. */
export function checkPaymentDraft(
  draft: PaymentDraft,
  { includeAmount, today = browserToday(), dateRefused = false }: { includeAmount: boolean; today?: string; dateRefused?: boolean }
): DraftCheck {
  // D109.6: the date box holds text that is not a real day, so `draft.paidDate` is the OLD date. Never save it.
  if (dateRefused && (draft.status === 'paid_in_full' || draft.status === 'part_paid')) {
    return { ok: false, code: 'PAID_DATE_INVALID', message: 'Fix the date paid first: it is not a real day.' };
  }
  const input = draftToInput(draft, { includeAmount });
  // A typed amount the form cannot read is a refusal, not "no amount yet".
  if (includeAmount && draft.status === 'part_paid' && draft.amount.trim() !== '' && input.amount === null) {
    return { ok: false, code: 'PART_PAID_AMOUNT_INVALID', message: 'The amount already paid must be a number greater than zero.' };
  }
  const parsed = parsePaymentOnReceipt(input, { requireAnswer: true, today });
  if (!parsed.ok) return parsed;
  return { ok: true, input, value: parsed.value as PaymentOnReceipt };
}

/** A stored answer, back into a draft to edit. Dates are stored as a day at UTC midnight. */
export function draftFromStored(stored: Partial<PaymentOnReceipt> & { paidDate?: string | null } | null | undefined): PaymentDraft {
  if (!stored || !stored.status) return { ...EMPTY_PAYMENT_DRAFT };
  return {
    status: stored.status,
    paidDate: storedDay(stored.paidDate) ?? '',
    method: stored.method || '',
    reference: stored.reference || '',
    amount: typeof stored.amount === 'number' ? String(stored.amount) : '',
  };
}

/** One line saying what the invoice's answer is, for the review screen. */
export function describePaymentAnswer(
  stored: Partial<PaymentOnReceipt> & { paidDate?: string | null } | null | undefined
): string {
  if (!stored || !stored.status) return 'Not answered yet';
  if (stored.status === 'unpaid') return 'Not paid yet: the supplier is owed the full amount';
  // The paid day is stored as that day's UTC midnight: print its UTC day, not the browser-zone evening before (D114.3).
  const day = formatDate(storedDay(stored.paidDate));
  const on = day ? ` on ${day}` : '';
  const by = stored.method ? ` by ${stored.method}` : '';
  const ref = stored.reference ? ` (ref ${stored.reference})` : '';
  if (stored.status === 'paid_in_full') return `Paid in full${on}${by}${ref}`;
  const amount = typeof stored.amount === 'number' ? ` ${stored.amount.toFixed(2)}` : ' (amount not entered yet)';
  return `Part paid:${amount}${on}${by}${ref}`;
}

/**
 * What Confirm & sync says when the "already paid?" answer is what stopped it, worded from the
 * refusal's CODE (the server's sentence is never shown). Null for any other refusal, which the page
 * presents as it always did. Nothing was added to stock in any of these.
 */
export function paymentAnswerDialog(code: string | undefined): DialogContent | null {
  switch (code) {
    case 'PAYMENT_ANSWER_REQUIRED':
      return {
        tone: 'blocked',
        title: 'Say whether this invoice has been paid',
        summary: 'This invoice has no answer yet to "Has it been paid?". It decides whether the supplier is owed the money.',
        hint: 'Choose Not paid yet, Paid in full or Part paid on this invoice, then confirm again. Nothing was added to stock.',
      };
    case 'PART_PAID_AMOUNT_REQUIRED':
      return {
        tone: 'blocked',
        title: 'Enter the amount already paid',
        summary: 'This invoice is marked Part paid, but no amount has been entered.',
        hint: 'Enter the amount already paid on this invoice, then confirm again. Nothing was added to stock.',
      };
    case 'PART_PAID_AMOUNT_TOO_LARGE':
      return {
        tone: 'blocked',
        title: 'The amount paid is too large for Part paid',
        summary: 'The amount already paid is not less than the invoice total.',
        hint: 'Choose Paid in full, or correct the amount, then confirm again. Nothing was added to stock.',
      };
    case 'PAYMENT_ANSWER_LOCKED':
      return {
        tone: 'blocked',
        title: 'This invoice is already confirmed',
        summary: 'Its bill has already been raised, so the answer can no longer be changed here.',
        hint: 'Record any payment on the bill in Accounts.',
      };
    default:
      return null;
  }
}
